/**
 * Histórico do que foi digitado no chat, como num terminal: com a caixa vazia, a
 * seta para cima traz a última linha enviada; cada toque a mais, enquanto o texto
 * não for editado, traz a anterior. A seta para baixo volta para as mais novas e,
 * depois da mais nova, deixa a caixa vazia de novo. Editou o texto: as setas
 * voltam a andar com o cursor.
 *
 * Fica por mesa e conversa, no navegador (sobrevive à troca de aba e ao recarregar).
 */

/** Linhas guardadas por conversa. */
export const HISTORY_SIZE = 50;
/** Conversas guardadas (as mais antigas saem). */
const MAX_CHANNELS = 40;
const STORAGE_KEY = 'rpgdesk.chatHistory';

/** Mais nova primeiro. */
type Store = Record<string, string[]>;

let memory: Store | null = null;

function store(): Store {
  if (memory) return memory;
  try {
    const raw = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}') as unknown;
    memory = {};
    if (raw && typeof raw === 'object')
      for (const [key, lines] of Object.entries(raw as Record<string, unknown>))
        if (Array.isArray(lines)) memory[key] = lines.filter((l): l is string => typeof l === 'string').slice(0, HISTORY_SIZE);
  } catch {
    memory = {};
  }
  return memory;
}

function save(): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store()));
  } catch {
    // sem localStorage (modo privado): o histórico vale só nesta sessão
  }
}

/** Linhas já enviadas nesta conversa, a mais nova primeiro. */
export function historyFor(key: string): readonly string[] {
  return store()[key] ?? [];
}

/** Guarda uma linha enviada (repetida em seguida não duplica). */
export function remember(key: string, line: string): void {
  const text = line.trim();
  if (!text) return;
  const all = store();
  const lines = all[key] ?? [];
  const next = lines[0] === text ? lines : [text, ...lines].slice(0, HISTORY_SIZE);
  // a conversa usada agora vai para o fim (as primeiras são as que saem)
  delete all[key];
  all[key] = next;
  const keys = Object.keys(all);
  for (const old of keys.slice(0, Math.max(0, keys.length - MAX_CHANNELS))) delete all[old];
  save();
}

/**
 * Um passo no histórico. `index` é a linha mostrada (-1 = a caixa do usuário, vazia).
 * Devolve o novo índice e o texto, ou `null` quando a seta não é do histórico (texto
 * editado ou nada mais para trás/para frente) e deve fazer o de sempre.
 */
export function step(lines: readonly string[], index: number, text: string, direction: 'older' | 'newer'): { index: number; text: string } | null {
  const shown = index >= 0 ? lines[index] : '';
  if (shown === undefined || text !== shown) return null;
  if (direction === 'older') {
    if (index + 1 >= lines.length) return null;
    return { index: index + 1, text: lines[index + 1]! };
  }
  if (index < 0) return null;
  return { index: index - 1, text: index - 1 >= 0 ? lines[index - 1]! : '' };
}

/** Só para testes: esquece o que está na memória (o próximo acesso relê o navegador). */
export function resetHistoryCache(): void {
  memory = null;
}
